// src/components/LanguageDropdown.tsx (With RTL support)
import { useState, useRef, useEffect } from 'react';
import { useLang } from '../context/TranslationProvider';
import { ChevronDown } from 'lucide-react';

export default function LanguageDropdown() {
  const { language, setLanguage, availableLanguages } = useLang();
  const [isOpen, setIsOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Get current language object
  const currentLanguageObj = availableLanguages.find(lang => lang.code === language);
  
  // Short display code with appropriate formatting
  const getDisplayText = (code: string): string => {
    const displayTexts: Record<string, string> = {
      en: 'EN',
      ar: 'ع',
      dv: 'ދ'
    };
    return displayTexts[code] || code.slice(0, 2).toUpperCase();
  };

  // Check if current language is RTL
  const isRTL = currentLanguageObj?.dir === 'rtl';

  return (
    <div className="relative" ref={dropdownRef}>
      <button
        onClick={() => setIsOpen(!isOpen)}
        className={`flex justify-center gap-2 dark:text-gray-300 text-[15px] items-center px-3 py-2 rounded-md hover:bg-gray-100 dark:hover:bg-gray-700 transition focus:outline-none focus:ring-2 focus:ring-green-500 focus:ring-offset-2 dark:focus:ring-offset-gray-800 ${
          isRTL ? 'flex-row-reverse' : ''
        }`}
        aria-label="Select language"
        aria-expanded={isOpen}
      >
        <span className="font-medium">{getDisplayText(language)}</span>
        <ChevronDown className={`h-4 w-4 transition-transform duration-200 ${isOpen ? 'rotate-180' : ''}`} />
      </button>

      {isOpen && (
        <>
          {/* Backdrop for mobile */}
          <div className="fixed inset-0 z-40" onClick={() => setIsOpen(false)} />
          
          {/* Dropdown menu */}
          <div className={`absolute ${isRTL ? 'left-0' : 'right-0'} mt-2 w-48 bg-white dark:bg-gray-800 rounded-lg shadow-lg z-50 overflow-hidden border border-gray-200 dark:border-gray-700`}>
            {availableLanguages.map((lang) => (
              <button
                key={lang.code}
                onClick={() => {
                  setLanguage(lang.code);
                  setIsOpen(false);
                }}
                className={`w-full text-left px-4 py-2.5 hover:bg-gray-100 dark:hover:bg-gray-700 transition flex items-center justify-between ${
                  lang.code === language 
                    ? 'bg-green-50 dark:bg-green-900/20 text-green-600 dark:text-green-400 font-semibold' 
                    : 'text-gray-700 dark:text-gray-300'
                } ${lang.dir === 'rtl' ? 'text-right flex-row-reverse' : ''}`}
                dir={lang.dir}
              >
                <div className={`flex items-center gap-2 ${lang.dir === 'rtl' ? 'flex-row-reverse' : ''}`}>
                  <span className="text-sm">{lang.name}</span>
                  <span className="text-xs text-gray-500 dark:text-gray-400">
                    ({getDisplayText(lang.code)})
                  </span>
                </div>
                {lang.code === language && (
                  <svg className="w-4 h-4 text-green-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                  </svg>
                )}
              </button>
            ))}
          </div>
        </>
      )}
    </div>
  );
}